/**
 * Сервис для автоматической инициализации дефолтных тегов
 * Создает предустановленные наборы тегов для каждого аккаунта
 * 
 * Соблюдает STYLEGUIDE.md:
 * - camelCase для frontend типов
 * - snake_case для database типов
 * - Корректный маппинг между слоями
 */

import { fetchClient } from '@/integrations/supabase/fetchClient';
import { logger } from '@/utils/logger';
import { TagCreate } from '@/types/entities/tag';
import { tagCreateToDbTagInsert } from '@/utils/mappers/tagMappers';

/**
 * Проверяет, является ли ошибка конфликтом (409 или duplicate key)
 */
function isConflictError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string; status?: number; statusCode?: number };
  if (e.code === '23505') return true; // PostgreSQL unique violation
  if (e.status === 409 || e.statusCode === 409) return true;
  if (typeof e.message === 'string' && (e.message.includes('duplicate') || e.message.includes('409') || e.message.includes('conflict'))) return true;
  return false;
}

// Дефолтные теги согласно требованиям пользователя
const DEFAULT_TAGS_CONFIG = {
  // Способы оплаты
  paymentMethods: [
    { name: 'Наличные', type: 'order' as const },
    { name: 'Карта', type: 'order' as const },
    { name: 'Перевод', type: 'order' as const }
  ],
  
  // Статусы оплаты
  paymentStatuses: [
    { name: 'Оплачено', type: 'order' as const },
    { name: 'Предоплата', type: 'order' as const },
    { name: 'Не оплачено', type: 'order' as const }
  ],
  
  // Статусы аренды/заказов (используются и в арендах и в заказах)
  orderRentalStatuses: [
    { name: 'Активный', type: 'general' as const },
    { name: 'К сборке', type: 'general' as const },
    { name: 'К сдаче', type: 'general' as const },
    { name: 'Просрочен', type: 'general' as const }
  ],
  
  // Теги состояния инвентаря
  inventoryConditions: [
    { name: 'Отлично', type: 'inventory' as const },
    { name: 'Нормально', type: 'inventory' as const },
    { name: 'Плохо', type: 'inventory' as const }
  ],
  
  // Теги статусов инвентаря
  inventoryStatuses: [
    { name: 'Доступен', type: 'inventory' as const },
    { name: 'В аренде', type: 'inventory' as const },
    { name: 'В ремонте', type: 'inventory' as const }
  ],
  
  // Теги магазина
  storeStatuses: [
    { name: 'В наличии', type: 'store' as const },
    { name: 'Под заказ', type: 'store' as const },
    { name: 'Нет в наличии', type: 'store' as const }
  ]
};

const allDefaultTagNames = [
  ...DEFAULT_TAGS_CONFIG.paymentMethods.map(tag => tag.name),
  ...DEFAULT_TAGS_CONFIG.paymentStatuses.map(tag => tag.name),
  ...DEFAULT_TAGS_CONFIG.orderRentalStatuses.map(tag => tag.name),
  ...DEFAULT_TAGS_CONFIG.inventoryConditions.map(tag => tag.name),
  ...DEFAULT_TAGS_CONFIG.inventoryStatuses.map(tag => tag.name),
  ...DEFAULT_TAGS_CONFIG.storeStatuses.map(tag => tag.name)
];

/**
 * Проверяет, инициализированы ли дефолтные теги для пользователя
 */
export async function checkDefaultTagsInitialized(userId: string): Promise<boolean> {
  try {
    // Проверяем наличие хотя бы одного дефолтного тега каждого типа
    const { data: existingTags, error } = await fetchClient
      .from('tags')
      .select('type, name')
      .eq('user_id', userId)
      .in('name', allDefaultTagNames);

    if (error) {
      logger.error('[checkDefaultTagsInitialized] Error checking tags:', error);
      return false;
    }

    // Считаем инициализированным, если есть хотя бы 4 из 6 ключевых тегов
    const foundTags = Array.isArray(existingTags) ? existingTags.length : 0;
    const isInitialized = foundTags >= 4;
    
    logger.info(`[checkDefaultTagsInitialized] Found ${foundTags} default tags for user ${userId}, initialized: ${isInitialized}`);
    return isInitialized;
  } catch (error) {
    logger.error('[checkDefaultTagsInitialized] Error:', error);
    return false;
  }
}

/**
 * Создает дефолтные теги для пользователя
 */
export async function initializeDefaultTags(userId: string): Promise<boolean> {
  try {
    logger.info(`[initializeDefaultTags] Initializing default tags for user ${userId}`);
    
    // Проверяем, не инициализированы ли уже теги
    const alreadyInitialized = await checkDefaultTagsInitialized(userId);
    if (alreadyInitialized) {
      logger.info(`[initializeDefaultTags] Default tags already initialized for user ${userId}`);
      return true;
    }

    // Собираем все теги для создания
    const tagsToCreate: TagCreate[] = [];
    
    // Создаем родительские теги для группировки
    const parentTags = [
      { name: '_payment_method_parent', type: 'order' as const, description: 'Способы оплаты' },
      { name: '_payment_status_parent', type: 'order' as const, description: 'Статусы оплаты' },
      { name: '_order_rental_status_parent', type: 'general' as const, description: 'Статусы заказов/аренды' },
      { name: '_inventory_condition_parent', type: 'inventory' as const, description: 'Состояние инвентаря' },
      { name: '_inventory_status_parent', type: 'inventory' as const, description: 'Статусы инвентаря' },
      { name: '_store_status_parent', type: 'store' as const, description: 'Статусы магазина' }
    ];

    // Добавляем родительские теги
    parentTags.forEach(tag => {
      tagsToCreate.push({
        name: tag.name,
        type: tag.type,
        description: tag.description,
        parentId: null,
        color: '#6b7280' // серый цвет для родительских тегов
      });
    });

    // Добавляем дочерние теги (пока без parentId, установим после создания родительских)
    Object.values(DEFAULT_TAGS_CONFIG).flat().forEach(tag => {
      tagsToCreate.push({
        name: tag.name,
        type: tag.type,
        description: null,
        parentId: null, // установим позже
        color: null
      });
    });

    // Создаем теги пакетами для лучшей производительности
    type CreatedTag = { id: string; name: string } & Record<string, unknown>;
    const createdTags: CreatedTag[] = [];
    let hadErrors = false;
    
    // Сначала создаем родительские теги
    for (const parentTag of parentTags) {
      // Проверяем, существует ли уже такой родительский тег
      const { data: existingParentTag } = await fetchClient
        .from('tags')
        .select('*')
        .eq('user_id', userId)
        .eq('name', parentTag.name)
        .eq('type', parentTag.type)
        .maybeSingle();
        
      if (existingParentTag) {
        logger.info(`[initializeDefaultTags] Parent tag ${parentTag.name} already exists, using existing`);
        createdTags.push(existingParentTag as unknown as CreatedTag);
        continue;
      }
      
      const tagCreate: TagCreate = {
        name: parentTag.name,
        type: parentTag.type,
        description: parentTag.description,
        parentId: null,
        color: '#6b7280' // серый цвет для родительских тегов
      };
      
      const dbData = tagCreateToDbTagInsert(tagCreate, userId);
      
      const { data: result, error } = await fetchClient
        .from('tags')
        .insert(dbData)
        .select()
        .single();

      if (error) {
        // Если ошибка конфликта (409/23505), тег уже существует - это нормально
        if (isConflictError(error)) {
          // Не логируем как ошибку - это ожидаемое поведение
          const { data: existingTag } = await fetchClient
            .from('tags')
            .select('*')
            .eq('user_id', userId)
            .eq('name', parentTag.name)
            .eq('type', parentTag.type)
            .maybeSingle();
          if (existingTag) {
            createdTags.push(existingTag as unknown as CreatedTag);
          }
          continue;
        }
        // Только реальные ошибки логируем как warn, не error
        if (process.env.NODE_ENV === 'development') {
          logger.warn(`[initializeDefaultTags] Error creating parent tag ${parentTag.name}:`, error);
        }
        hadErrors = true;
        continue;
      }
      
      createdTags.push(result as unknown as CreatedTag);
      logger.info(`[initializeDefaultTags] Created parent tag: ${parentTag.name}`);
    }

    // Создаем карту родительских тегов для связывания
    const parentTagsMap: Record<string, string> = {};
    createdTags.forEach(tag => {
      if (tag.name.includes('_parent')) {
        parentTagsMap[tag.name] = tag.id;
      }
    });

    // Теперь создаем дочерние теги с правильными parentId
    const childTagsToCreate = [
      ...DEFAULT_TAGS_CONFIG.paymentMethods.map(tag => ({
        ...tag,
        parentId: parentTagsMap['_payment_method_parent'] || null
      })),
      ...DEFAULT_TAGS_CONFIG.paymentStatuses.map(tag => ({
        ...tag,
        parentId: parentTagsMap['_payment_status_parent'] || null
      })),
      ...DEFAULT_TAGS_CONFIG.orderRentalStatuses.map(tag => ({
        ...tag,
        parentId: parentTagsMap['_order_rental_status_parent'] || null
      })),
      ...DEFAULT_TAGS_CONFIG.inventoryConditions.map(tag => ({
        ...tag,
        parentId: parentTagsMap['_inventory_condition_parent'] || null
      })),
      ...DEFAULT_TAGS_CONFIG.inventoryStatuses.map(tag => ({
        ...tag,
        parentId: parentTagsMap['_inventory_status_parent'] || null
      })),
      ...DEFAULT_TAGS_CONFIG.storeStatuses.map(tag => ({
        ...tag,
        parentId: parentTagsMap['_store_status_parent'] || null
      }))
    ];

    // Создаем дочерние теги
    for (const childTag of childTagsToCreate) {
      // Проверяем, существует ли уже такой тег
      const { data: existingTag } = await fetchClient
        .from('tags')
        .select('*')
        .eq('user_id', userId)
        .eq('name', childTag.name)
        .eq('type', childTag.type)
        .maybeSingle();
        
      if (existingTag) {
        logger.info(`[initializeDefaultTags] Tag ${childTag.name} already exists, skipping`);
        continue;
      }
      
      const tagCreate: TagCreate = {
        name: childTag.name,
        type: childTag.type,
        description: null,
        parentId: childTag.parentId,
        color: null
      };
      
      const dbData = tagCreateToDbTagInsert(tagCreate, userId);
      
      const { data: result, error } = await fetchClient
        .from('tags')
        .insert(dbData)
        .select()
        .single();

      if (error) {
        // Если ошибка конфликта (409/23505), тег уже существует - это нормально, не логируем
        if (isConflictError(error)) {
          continue;
        }
        // Только реальные ошибки логируем как warn в dev mode
        if (process.env.NODE_ENV === 'development') {
          logger.warn(`[initializeDefaultTags] Error creating child tag ${childTag.name}:`, error);
        }
        continue;
      }
      
      logger.info(`[initializeDefaultTags] Created child tag: ${childTag.name} (type: ${childTag.type})`);
    }

    // Всегда возвращаем true, даже если были ошибки дубликатов
    // Это не должно блокировать авторизацию пользователя
    logger.info(`[initializeDefaultTags] Default tags initialization completed for user ${userId}`);
    return true;
  } catch (error) {
    // Не логируем как error в production - это не критично
    if (process.env.NODE_ENV === 'development') {
      logger.warn('[initializeDefaultTags] Error:', error);
    }
    return false;
  }
}

/**
 * Автоматически инициализирует дефолтные теги при первом входе пользователя
 * Вызывается из AuthContext или при загрузке настроек
 */
export async function ensureDefaultTags(userId: string): Promise<void> {
  try {
    const isInitialized = await checkDefaultTagsInitialized(userId);
    
    if (!isInitialized) {
      logger.info(`[ensureDefaultTags] Initializing default tags for user ${userId}`);
      await initializeDefaultTags(userId);
    } else {
      logger.info(`[ensureDefaultTags] Default tags already exist for user ${userId}`);
    }
  } catch (error) {
    logger.error('[ensureDefaultTags] Error ensuring default tags:', error);
    // Не выбрасываем ошибку, чтобы не блокировать работу приложения
  }
}

/**
 * Получает список дефолтных тегов для отображения в UI
 */
export function getDefaultTagsConfig() {
  return DEFAULT_TAGS_CONFIG;
}
